import React, { useState, useEffect } from "react";
import axios from "axios";
import { useUser } from '../components/Context/UserContext';
import { Table, Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, Container } from "reactstrap";
import { Link } from "react-router-dom";
import "../styles/ViewAllUsers.css";
import DashboardLayout from "./DashboardLayout";

const ViewAllUsers = () => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [users, setUsers] = useState([]);
    const [sortBy, setSortBy] = useState("");
    const [sortOrder, setSortOrder] = useState("asc"); // asc or desc
    const { user } = useUser();
    const isPoAdmin = user?.roles?.some(r => r.role === "PO Admin");
    const [showCard, setShowCard] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const usersPerPage = 6; // Only 6 users per page
    const [showModal, setShowModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [activeTab, setActiveTab] = useState("details");
    const [comment, setComment] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [searchInput, setSearchInput] = useState(""); // raw field value (debounced into searchTerm) 
    const [selectedStatus, setSelectedStatus] = useState("all");

    useEffect(() => {
        axios.get("/api/userregistration")
            .then(response => {
                const data = response.data;
                if (Array.isArray(data)) {
                    setUsers(data);
                } else if (data && Array.isArray(data.$values)) {
                    setUsers(data.$values);
                } else if (data && Array.isArray(data.data)) {
                    setUsers(data.data);
                } else {
                    console.error("Unexpected response shape:", data);
                    setUsers([]);
                }
            })
            .catch(error => {
                console.error("Error fetching users:", error);
                setUsers([]);
            });
    }, []);

    useEffect(() => {
        const dark = localStorage.getItem("darkMode") === "true";
        document.body.classList.toggle("dark-mode", dark);
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "Invalid Date";
        return `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
    };
    const handleSort = (column) => {
        const sortedUsers = [...users].sort((a, b) => {
            let valueA, valueB;

            if (column === "isApproved") {
                valueA = String(a.isApproved).toLowerCase();
                valueB = String(b.isApproved).toLowerCase();
            } else if (column === "createDate") {
                valueA = new Date(a.createDate);
                valueB = new Date(b.createDate);
            } else if (column === "nameOfCompany") {
                valueA = a.nameOfCompany?.toLowerCase() || "";
                valueB = b.nameOfCompany?.toLowerCase() || "";
            }

            if (sortOrder === "asc") {
                return valueA > valueB ? 1 : -1;
            } else {
                return valueA < valueB ? 1 : -1;
            }
        });

        setUsers(sortedUsers);
        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        setSortBy(column);
    };


    // Pagination calculations
    // Search Filter
    const filteredUsers = (Array.isArray(users) ? users : []).filter((user) => {
        const matchesSearch = user.nameOfCompany
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase());

        const matchesStatus =
            selectedStatus === "all" ||
            Number(user.isApproved) === Number(selectedStatus);

        return matchesSearch && matchesStatus;
    });


    // Pagination calculations
    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
    const startIndex = (currentPage - 1) * usersPerPage;

    const usersForPage = filteredUsers.slice(
        startIndex,
        startIndex + usersPerPage
    );
    const handlePageChange = (type) => {
        if (type === "first") setCurrentPage(1);
        if (type === "prev" && currentPage > 1) setCurrentPage(currentPage - 1);
        if (type === "next" && currentPage < totalPages) setCurrentPage(currentPage + 1);
        if (type === "last") setCurrentPage(totalPages);
    };

    const handleReviewClick = (indexInPage) => {
        if (!isPoAdmin) return;
        const actualIndex = startIndex + indexInPage;
        setSelectedUser({ ...users[actualIndex], index: actualIndex });
        setComment("");
        setShowModal(true);
        setIsEditing(false);
    };

    const handleInputChange = (e, field) => {
        setSelectedUser({ ...selectedUser, [field]: e.target.value });
    };

    const handleSave = async () => {
        if (!selectedUser) return;

        try {
            const payload = {
                isApproved:
                    selectedUser.isApproved === "approved"
                        ? 1
                        : selectedUser.isApproved === "rejected"
                            ? 2
                            : 0,

                comments: comment,
                approverId: user?.id ? String(user.id) : null   // ApproverId is a string field
            };

            await axios.put(`/api/userregistration/${selectedUser.id}`, payload);

            const updatedUsers = [...users];
            updatedUsers[selectedUser.index] = {
                ...selectedUser,
                isApproved: payload.isApproved,
                comments: comment
            };
            setUsers(updatedUsers);
        } catch (error) {
            console.error("Error updating approval status:", error);
            alert("Failed to save. Check console.");
        }

        setShowModal(false);
    };

    const handleApprove = () => {
        if (selectedUser && selectedUser.index !== undefined) {
            const updatedUsers = [...users];
            updatedUsers[selectedUser.index].isApproved = true;
            setUsers(updatedUsers);
        }
        setShowModal(false);
    };

    const handleReject = () => {
        if (selectedUser && selectedUser.index !== undefined) {
            const updatedUsers = [...users];
            updatedUsers[selectedUser.index].isApproved = false;
            setUsers(updatedUsers);
        }
        setShowModal(false);
    };
    const statusMapping = {
        All: "all",
        Pending: 0,
        Approved: 1,
        Rejected: 2
    };

    return (
        <DashboardLayout>
            <div className="view-users-container">
                <Container fluid style={{ marginTop: "-85px", paddingTop: "0" }}>

                    <h2 className="page-title" style={{ marginTop: 0, marginBottom: '15px', color: '#0a4275', paddingTop: '50px' }}>Users List</h2>

                    {/* 🔹 Search & Filter Row */}
                    <div

                        className="vendor-toolbar"

                        style={{

                            display: "flex",

                            width: "100%",

                            justifyContent: "space-between",

                            alignItems: "center",

                            flexWrap: "nowrap",

                            marginBottom: "20px",

                        }}
                    >

                        {/* LEFT CORNER - SEARCH */}
                        <div

                            style={{

                                display: "flex",

                                alignItems: "center",

                            }}
                        >
                            <div

                                className="vendor-search-box invoice-search-box"

                                style={{

                                    width: "350px",

                                }}
                            >
                                <span className="vendor-search-icon">

                                    🔍
                                </span>

                                <Input

                                    type="text"

                                    placeholder="Search by Company Name..."

                                    value={searchTerm}

                                    onChange={(e) => {

                                        setSearchTerm(e.target.value);

                                        setCurrentPage(1);

                                    }}

                                    className="vendor-search-input"

                                />
                            </div>
                        </div>

                        {/* RIGHT CORNER - STATUS */}
                        <div

                            style={{

                                display: "flex",

                                alignItems: "center",

                                gap: "10px",

                            }}
                        >
                            <span style={{ whiteSpace: "nowrap" }}>

                                Approved Status:
                            </span>

                            <select

                                value={selectedStatus}

                                onChange={(e) => {

                                    const value = e.target.value;

                                    setSelectedStatus(

                                        value === "all" ? "all" : Number(value)

                                    );

                                    setCurrentPage(1);

                                }}

                                style={{

                                    width: "180px",

                                    height: "36px",

                                }}
                            >
                                <option value="all">All</option>
                                <option value={statusMapping.Pending}>⏳ Pending</option>
                                <option value={statusMapping.Approved}>✔️ Approved</option>
                                <option value={statusMapping.Rejected}>❌ Rejected</option>
                            </select>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <Table striped bordered hover className="users-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                        <th onClick={() => handleSort("nameOfCompany")} style={{ cursor: "pointer" }}>
                                            Company Name {sortBy === "nameOfCompany"
                                                ? (sortOrder === "asc" ? "▲" : "▼")
                                                : "⇅"}
                                        </th>
                                    <th>Contact Person</th>
                                    <th>Email ID</th>
                                    <th>Contact No.</th>
                                    <th>Address</th>
                                    <th onClick={() => handleSort("isApproved")} style={{ cursor: "pointer" }}>
                                        Approval Status {sortBy === "isApproved"
                                            ? (sortOrder === "asc" ? "▲" : "▼")
                                            : "⇅"}
                                    </th>
                                    <th onClick={() => handleSort("createDate")} style={{ cursor: "pointer" }}>
                                        Create Date {sortBy === "createDate"
                                            ? (sortOrder === "asc" ? "▲" : "▼")
                                            : "⇅"}
                                    </th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {usersForPage.length > 0 ? (
                                    usersForPage.map((user, index) => (
                                        <tr key={index}>
                                            <td>{startIndex + index + 1}</td>
                                            <td>{user.nameOfCompany || "N/A"}</td>
                                            <td>{user.contactPersonName || "N/A"}</td>
                                            <td>{user.emailId || "N/A"}</td>
                                            <td>{user.contactNumber || "N/A"}</td>
                                            <td>{user.address || "N/A"}</td>
                                            <td>
                                                {Number(user.isApproved) === 1
                                                    ? "Approved"
                                                    : Number(user.isApproved) === 2
                                                        ? "Rejected"
                                                        : "Pending"}
                                            </td>                                            <td>{user.createDate ? formatDate(user.createDate) : "N/A"}</td>
                                            <td>
                                                <button
                                                    className="action-btn"
                                                    onClick={() => handleReviewClick(index)}
                                                    disabled={!isPoAdmin}
                                                    title={!isPoAdmin ? "Only PO Admin can review" : ""}
                                                >
                                                    Review
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="9" style={{ textAlign: "center", padding: "20px" }}>
                                            No vendors found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <div className="pagination">
                        <button onClick={() => handlePageChange("first")} disabled={currentPage === 1}>First</button>
                        <button onClick={() => handlePageChange("prev")} disabled={currentPage === 1}>Previous</button>
                        <span>Page {currentPage} of {totalPages}</span>
                        <button onClick={() => handlePageChange("next")} disabled={currentPage === totalPages}>Next</button>
                        <button onClick={() => handlePageChange("last")} disabled={currentPage === totalPages}>Last</button>
                        </div>
                </Container>  
            </div>

            <Modal isOpen={showModal} toggle={() => setShowModal(false)} centered>
                <ModalHeader toggle={() => setShowModal(false)}>User Details</ModalHeader>
                <ModalBody>
                    <p><strong>Company Name:</strong> {selectedUser?.nameOfCompany}</p>
                    <div className="approval-section">
                        <label><strong>Approval Status:</strong></label><span className="required-field">*</span>
                        <Input
                            type="select"
                            className="approval-dropdown"
                            value={
                                Number(selectedUser?.isApproved) === 1 || selectedUser?.isApproved === "approved"
                                    ? "approved"
                                    : Number(selectedUser?.isApproved) === 2 || selectedUser?.isApproved === "rejected"
                                        ? "rejected"
                                        : "pending"
                            }

                            onChange={(e) => setSelectedUser({ ...selectedUser, isApproved: e.target.value })}
                        >
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </Input>
                    </div>
                    <div className="comment-section">
                        <label><strong>Comment:</strong></label><span className="required-field">*</span>
                        <Input
                            type="textarea"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Enter your comment here..."
                        />
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="success" onClick={handleSave}>Save</Button>
                    <Button color="danger" onClick={() => setShowModal(false)}>Close</Button>
                </ModalFooter>
            </Modal>
            {showCard && (

                <div className="user-card">

                    <p><strong>Name of Company:</strong></p>

                    <input type="text" value={user.nameOfCompany} readOnly className="user-info-input" />

                    <p><strong>Contact Person:</strong></p>

                    <input type="text" value={user.contactPersonName} readOnly className="user-info-input" />

                    <p><strong>Email:</strong></p>

                    <input type="text" value={user.emailId} readOnly className="user-info-input" />

                    <p><strong>Contact Number:</strong></p>

                    <input type="text" value={user.contactNumber} readOnly className="user-info-input" />

                    <p><strong>Address:</strong></p>

                    <input type="text" value={user.address} readOnly className="user-info-input" />

                    <div className="text-center mt-2">

                        <p><Link to="/login" className="text-decoration-none">Log out</Link></p>

                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default ViewAllUsers;
